import MatchedMap from 'utilities/src/matchedMap';
import { TBoxTypeEnum, TFormField } from '../../shared/utils/types';
import { InputStateEnum } from '../../shared/utils/state';
import { InputThemeEnum } from '../../shared/utils/themes';
import { ICheckboxProps } from '../_props';

/**
 * State Classes
 */

const stateClasses = new MatchedMap([
  [InputStateEnum.Default, ''],
  [InputStateEnum.Success, ''],
  [InputStateEnum.Error, ''],
  [InputStateEnum.Disabled, 'pointer-events-none opacity-30'],
]);

const checkboxClasses = 'flex flex-wrap';

/**
 * Checkbox Classes
 */

const checkboxInputClasses = 'opacity-0 w-0 h-0';
const checkboxInputCheckedClasses = '[&:checked~label>div_svg]:visible';

const checkboxLabelClasses = 'flex items-center gap-3 cursor-pointer';

const checkboxBoxClasses =
  'flex items-center justify-center w-[1.125rem] h-[1.125rem] rounded-sm border-2 border-solid';

const radioBoxClasses =
  'flex items-center justify-center w-[1.125rem] h-[1.125rem] rounded-full border-2 border-solid';

const boxTypeClasses = new MatchedMap([
  [TBoxTypeEnum.Checkbox, checkboxBoxClasses],
  [TBoxTypeEnum.Radio, radioBoxClasses],
]);

const checkboxLabelTextClasses = 'text-[1.05rem] leading-6 font-medium';

const checkboxIconClasses = 'invisible stroke-[0.438rem] w-[1.125rem] h-[1.125rem] p-[0.125rem]';

/**
 * Theme Classes
 */

const checkboxBoxThemeClasses = new MatchedMap([
  [
    InputThemeEnum.Dark,
    'border-white hover:ring hover:ring-offset-2 hover:ring-site-primary-200 hover:ring-offset-site-primary-200',
  ],
  [
    InputThemeEnum.Light,
    'border-site-neutrals-700 hover:ring hover:ring-offset-2 hover:ring-site-primary-50 hover:ring-offset-site-primary-50',
  ],
]);

const radioBoxThemeClasses = new MatchedMap([
  [InputThemeEnum.Dark, 'border-white'],
  [InputThemeEnum.Light, 'border-site-neutrals-700'],
]);

const boxTypeThemeClasses = new MatchedMap([
  [TBoxTypeEnum.Checkbox, checkboxBoxThemeClasses],
  [TBoxTypeEnum.Radio, radioBoxThemeClasses],
]);

const checkboxLabelTextThemeClasses = new MatchedMap([
  [InputThemeEnum.Dark, 'text-white'],
  [InputThemeEnum.Light, 'text-site-neutrals-700'],
]);

const checkboxIconThemeClasses = new MatchedMap([
  [InputThemeEnum.Dark, 'fill-black stroke-black'],
  [InputThemeEnum.Light, 'fill-white stroke-white'],
]);

const radioIconThemeClasses = new MatchedMap([
  [InputThemeEnum.Dark, 'fill-black stroke-black'],
  [InputThemeEnum.Light, 'fill-white stroke-white'],
]);

const checkboxIconTheme = new MatchedMap([
  [TBoxTypeEnum.Checkbox, checkboxIconThemeClasses],
  [TBoxTypeEnum.Radio, radioIconThemeClasses],
]);

export default function checkBoxCommonTheme({
  state = 'Default',
  type = 'Checkbox',
  theme = 'Light',
}: Pick<ICheckboxProps<TFormField>, 'state' | 'type' | 'theme'>) {
  const fieldsetClasses = checkboxClasses.concat(' ', stateClasses.get(InputStateEnum[state]));
  const inputClasses = checkboxInputClasses.concat(' ', checkboxInputCheckedClasses);
  const boxDefaultClasses = boxTypeClasses.get(TBoxTypeEnum[type]);
  const boxThemeClasses = boxTypeThemeClasses.get(TBoxTypeEnum[type]).get(InputThemeEnum[theme]);
  const boxClasses = boxDefaultClasses.concat(' ', boxThemeClasses);
  const labelTextClasses = checkboxLabelTextThemeClasses
    .get(InputThemeEnum[theme])
    .concat(' ', checkboxLabelTextClasses);

  const iconClasses = checkboxIconTheme
    .get(TBoxTypeEnum[type])
    .get(InputThemeEnum[theme])
    .concat(' ', checkboxIconClasses);

  return {
    fieldsetClasses,
    inputClasses,
    checkboxLabelClasses,
    boxClasses,
    labelTextClasses,
    iconClasses,
  };
}
